import React, { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ForceApplicationName, ForceSegmentName, ForceStepName, StepToParam } from '@/api/appSolutionBuilder/search';
import { AppSolutionBuilderStep } from '@/api/appSolutionBuilder/types';
import { Button, Icon, Modal } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { AccountId, Area, PageType } from '@/generated/data.schemas';
import { useGetLayout } from '@/generated/layout';
import { useGetTranslations } from '@/generated/translations';
import { SwitchCustomer } from './SwitchCustomer';

interface SelectionByApplicationProps {
	accountId: AccountId;
	selectedCustomerId: AccountId;
	segment: string;
	application: string;
}

export const SelectionByApplication: React.FunctionComponent<SelectionByApplicationProps> = ({
	accountId,
	selectedCustomerId,
	segment,
	application,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: layout } = useGetLayout(segmentationId);
	const navigate = useNavigate();

	const [isOpen, setIsOpen] = React.useState(false);

	const isCurrentCustomer: boolean = accountId === selectedCustomerId;

	const staticLinks = layout?.data.staticLinks;

	const appSolutionUrl = useMemo(
		() => staticLinks?.find((item) => item.pageType === PageType.AppSolutionBuilder)?.url,
		[staticLinks],
	);

	const appSolutionNavigationUrl = useMemo(() => {
		if (!appSolutionUrl) return null;

		const appSolutionParams = new URLSearchParams({
			[ForceStepName]: StepToParam[AppSolutionBuilderStep.Specifications],
			[ForceSegmentName]: segment,
			[ForceApplicationName]: application,
		});

		return {
			pathname: appSolutionUrl,
			search: `?${appSolutionParams.toString()}`,
			state: {
				area: Area.StaticPages,
				pageType: PageType.AppSolutionBuilder,
			},
		};
	}, [appSolutionUrl, segment, application]);

	const handleNavigation = useCallback(() => {
		if (appSolutionNavigationUrl) {
			const { pathname, search, state } = appSolutionNavigationUrl;

			navigate({ pathname, search }, { state });
		}
	}, [appSolutionNavigationUrl, navigate]);

	const handleOpen = React.useCallback((): void => {
		setIsOpen(true);
	}, []);

	const handleClose = React.useCallback((): void => {
		setIsOpen(false);
	}, []);

	return (
		<>
			<Button isFullWidth onClick={isCurrentCustomer ? handleNavigation : handleOpen}>
				{translations?.data.applications.goToSelectionByApp}
				<Icon name={'arrowOut'} size={'md'} rotate={'45'} />
			</Button>

			{isOpen && (
				<Modal open={isOpen} size={'sm'} onClose={handleClose}>
					<SwitchCustomer accountId={accountId} handleNavigation={handleNavigation} setIsOpen={setIsOpen} />
				</Modal>
			)}
		</>
	);
};
